/**
 * Development review counts — the runtime half.
 *
 * The counts themselves are rendered by Liquid (snippets/review-count.liquid), so a page arrives
 * with the right number already in it and nothing flickers. This file does two small jobs:
 *
 *   1. (Safety net removed 2026-09-25: the custom counts stay on the published theme as well.)
 *
 *   2. The one-off link from the app ("Open store with this count"), which applies a count in
 *      this browser only, for trying a number without saving it.
 *
 *   3. Date cascade. Rewrites review dates with relative times (e.g. "2h ago", "3d ago") using
 *      a deterministic gap per review index so the pattern is stable across load-more pages.
 */
(() => {
  const KEY = 'nt_review_preview';
  const fmt = (n) => Number(n).toLocaleString('en-US');

  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  };
  const write = (v) => {
    try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ }
  };

  const handleFromPath = (path) => {
    const m = /\/products\/([^/?#]+)/.exec(path || '');
    return m ? decodeURIComponent(m[1]) : null;
  };

  const role = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '';

  /* ------------------------------------------------------------ safety net */
  // Removed 2026-09-25 at the owner's request: the custom counts must stay on the published theme
  // too, so nothing is put back to the real number when Shopify.theme.role is "main". (This block
  // used to rewrite the header, tab, toolbar and title-row counts to the real total on the live
  // theme while the star bars kept the custom split.)

  /* --------------------------------------------- one-off link from the app */
  const params = new URLSearchParams(location.search);
  if (params.has('pr_preview')) {
    const count = Math.max(0, Number(params.get('pr_preview')) || 0);
    const dist = (params.get('pr_dist') || '90,6,2,1,1').split(',').map(Number);
    const handle = handleFromPath(location.pathname);
    const state = read() || { items: {} };
    if (handle) state.items[handle] = { count, dist: dist.length === 5 ? dist : [90, 6, 2, 1, 1] };

    // store cascade settings if present
    if (params.has('pr_cascade') || params.has('pr_gap_min')) {
      const cascadeRaw = params.get('pr_cascade') || '';
      state.cascade = {
        steps: cascadeRaw ? cascadeRaw.split(',').map(Number).filter((n) => !isNaN(n) && n > 0) : [],
        gapMin: Math.max(0, Number(params.get('pr_gap_min')) || 48),
        gapMax: Math.max(0, Number(params.get('pr_gap_max')) || 120),
        oldest: Math.max(1, Number(params.get('pr_oldest')) || 8760),
        photoOnly: params.get('pr_photo_only') === '1',
      };
    }

    write(state);
  }

  const local = read();
  // The one-off "Open store with this count" state (localStorage) is a development tool: on the
  // published theme it is ignored, so nothing repaints the counts Liquid already rendered from the
  // saved overrides (a stale value here made the header flip a moment after load).
  const items = role === 'main' ? {} : ((local && local.items) || {});
  // Check if there are saved overrides from the metafield (count or cascade)
  const savedOverrides = (window.theme && window.theme.reviewOverrides) || {};
  if (!Object.keys(items).length && !Object.keys(savedOverrides).length) return;

  const forHandle = (handle) => (handle ? items[handle] : null);

  /** Star counts implied by a distribution, always summing to the total. */
  const split = (count, dist) => {
    const total = dist.reduce((a, b) => a + b, 0) || 1;
    const out = dist.map((w) => Math.round((w / total) * count));
    out[0] += count - out.reduce((a, b) => a + b, 0);
    return out;
  };

  const average = (counts) => {
    const n = counts.reduce((a, b) => a + b, 0);
    return n ? counts.reduce((a, c, i) => a + c * (5 - i), 0) / n : 0;
  };

  /* -------------------------------------------------- date cascade helpers */
  const relativeTime = (ms) => {
    const h = Math.max(1, ms / 3600000);
    if (h < 24) return Math.floor(h) + 'h ago';
    // Days up to 60, not weeks: week labels collapse many distinct dates into the same string
    // (14d and 19d both read "2w ago"), which makes a seeded cascade look obviously generated.
    const d = Math.floor(h / 24);
    if (d < 60) return d + 'd ago';
    const mo = Math.floor(d / 30);
    if (mo < 12) return mo + ' month' + (mo > 1 ? 's' : '') + ' ago';
    const yr = Math.floor(d / 365);
    return yr + ' year' + (yr > 1 ? 's' : '') + ' ago';
  };

  // Simple deterministic pseudo-random seeded by index (no Math.random, consistent across page loads)
  const seededRand = (seed) => {
    let x = (seed * 1664525 + 1013904223) >>> 0;
    x ^= x >>> 16;
    x = Math.imul(x, 0x45d9f3b);
    x ^= x >>> 16;
    return (x >>> 0) / 0xFFFFFFFF;
  };

  /* ------------------------------------------------- helpful count tables
     Duplicated verbatim in sections/product-reviews.liquid, which pre-renders the first page
     from them so no real count is ever painted first. Change one, change the other. */
  const HELP_PCT = [100, 77, 59, 68, 41, 50, 32, 45, 23, 36, 18, 27, 14, 31, 21, 16, 25, 11, 19, 13, 9, 17, 6, 12];
  const HELP_ROLL = [0, 58, 12, 91, 34, 73, 5, 47, 88, 21, 66, 39, 95, 8, 52, 77, 29, 61, 15, 84, 43, 97, 25, 70];
  const HELP_MAG = [100, 60, 85, 30, 70, 45, 90, 25, 55, 75, 35, 65, 20, 80, 50, 40, 95, 15, 60, 30, 70, 45, 85, 55];

  const applyHelpfulCounts = () => {
    const handle = handleFromPath(location.pathname);
    const savedOverride = handle && window.theme && window.theme.reviewOverrides
      && window.theme.reviewOverrides[handle];
    const hc = savedOverride && savedOverride.helpful;
    if (!hc || !hc.maxLike) return;

    const maxLike = hc.maxLike;
    const maxDislike = hc.maxDislike || 0;
    // Raw percent, not a fraction: it is compared against HELP_ROLL, which is 0-99.
    const dislikeRate = hc.dislikeRate || 0;

    const reviewItems = [...document.querySelectorAll('.pr-item')].slice(0, HELP_PCT.length);
    reviewItems.forEach((item, i) => {
      let likeCount, dislikeCount;
      if (i === 0) {
        likeCount = maxLike;
        dislikeCount = maxDislike;
      } else {
        // Integer maths in the same order as the Liquid, so a review rendered server side and
        // the same index rendered by "Load more" can never disagree by a rounding step.
        likeCount = Math.floor((maxLike * HELP_PCT[i]) / 100);
        // maxLike - 1: the cap belongs to the top review alone, otherwise several in a row tie at it
        likeCount = Math.min(maxLike - 1, Math.max(0, likeCount));
        dislikeCount = HELP_ROLL[i] < dislikeRate
          ? Math.floor((maxDislike * HELP_MAG[i]) / 100)
          : 0;
      }
      // Read stored vote (set by product-reviews.js on click) to add it on top of base count
      const helpBox = item.querySelector('[data-review-helpful]');
      let storedVote = null;
      if (helpBox) {
        const rid = helpBox.dataset.reviewId;
        if (rid) { try { storedVote = localStorage.getItem('nt_vote_' + rid); } catch (e) {} }
      }

      const upCount = item.querySelector('.pr-help__btn[data-vote="up"] [data-vote-count]');
      const downCount = item.querySelector('.pr-help__btn[data-vote="down"] [data-vote-count]');
      if (upCount) upCount.textContent = likeCount + (storedVote === 'up' ? 1 : 0);
      if (downCount) downCount.textContent = dislikeCount + (storedVote === 'down' ? 1 : 0);
    });
  };

  const applyDateCascade = () => {
    // Prefer saved metafield override (set via "Save for dev themes"), fall back to URL-based localStorage
    const handle = handleFromPath(location.pathname);
    const savedCascade = handle && window.theme && window.theme.reviewOverrides
      && window.theme.reviewOverrides[handle] && window.theme.reviewOverrides[handle].cascade;
    const dc = savedCascade || (local && local.cascade);
    if (!dc || (!dc.steps.length && !dc.gapMin)) return;

    const now = Date.now();
    const gapMinMs = dc.gapMin * 3600000;
    const gapMaxMs = dc.gapMax * 3600000;
    const oldestMs = dc.oldest * 3600000;

    // All visible review items
    const allItems = [...document.querySelectorAll('.pr-item')];
    const items = dc.photoOnly
      ? allItems.filter((el) => el.querySelector('.pr-item__photo, [data-photo], img'))
      : allItems;

    let cumMs = 0;

    items.forEach((item, i) => {
      if (i < dc.steps.length) {
        cumMs = dc.steps[i] * 3600000;
      } else {
        const r = seededRand(i + 1);
        cumMs += gapMinMs + r * (gapMaxMs - gapMinMs);
      }

      const ageMs = Math.min(cumMs, oldestMs);
      const tsMs = now - ageMs;
      const label = relativeTime(ageMs);

      item.querySelectorAll('.pr-item__date').forEach((el) => {
        el.textContent = label;
        if (el.tagName === 'TIME') el.setAttribute('datetime', new Date(tsMs).toISOString());
        el.removeAttribute('data-cascade-pending');
      });
    });
  };

  const paint = () => {
    document.querySelectorAll('[data-product-reviews]').forEach((root) => {
      const cfg = forHandle(handleFromPath(location.pathname));
      if (!cfg) return;
      const counts = split(cfg.count, cfg.dist);

      const avgEl = root.querySelector('.pr__avg');
      if (avgEl) avgEl.textContent = average(counts).toFixed(1);

      const countEl = root.querySelector('.pr__count');
      if (countEl) countEl.textContent = `Based on ${fmt(cfg.count)} ${cfg.count === 1 ? 'review' : 'reviews'}`;

      root.querySelectorAll('.pr__bar-row').forEach((row, i) => {
        const n = counts[i] || 0;
        const c = row.querySelector('.pr__bar-count');
        const fill = row.querySelector('.pr__bar-fill');
        if (c) c.textContent = fmt(n);
        if (fill) fill.style.setProperty('--w', `${cfg.count ? (n / cfg.count) * 100 : 0}%`);
      });

      const tab = root.querySelector('[data-tab="reviews"]');
      if (tab) tab.textContent = `Reviews (${fmt(cfg.count)})`;
      const toolbar = root.querySelector('.pr__toolbar-count');
      if (toolbar) toolbar.textContent = `${fmt(cfg.count)} reviews`;
    });

    document.querySelectorAll('.stars__count').forEach((el) => {
      const link = el.closest('a[href*="/products/"]')
        || el.closest('.card, .card-product, li, article')?.querySelector('a[href*="/products/"]');
      const cfg = forHandle(handleFromPath(link ? link.getAttribute('href') : location.pathname));
      if (cfg) el.textContent = fmt(cfg.count);
    });

    applyDateCascade();
    applyHelpfulCounts();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paint, { once: true });
  else paint();
  document.addEventListener('shopify:section:load', paint);

  // Re-apply after load-more adds new reviews
  document.addEventListener('reviews:loaded', () => {
    applyDateCascade();
    applyHelpfulCounts();
  });
})();
